<template>
  <div v-if="availableParticipants && availableParticipants.length > 0">
    <!-- Add Extra Card option -->
    <h2 class="text-normal-emphasis font-bold">Extra Cards</h2>
    <div class="flex justify-between items-center gap-2 ">
      <div>
        <h2 class="text-normal-emphasis font-bold">Add Extra Cards to all</h2>
      </div>
      <div>
        <AppInputCheckbox name="selectAll" type="checkbox" v-model="areAllSelected"
          @update:modelValue="toggleAllParticipant" />

      </div>
    </div>

    <!-- Hands list (only shown when showHandsList is true) -->
    <div>
      <template v-for="(participant, index) in availableParticipantsToShow" :key="`assign-participant-${index}`">
        <div class="flex justify-between items-center gap-4 mb-4">
          <div class="flex justify-between items-center py-2 px-3 rounded-lg border-2 transition-colors flex-1" :class="{
            'border-action bg-white': isParticipantSelected(participant.id as string),
            'border-gray-200 bg-white': !isParticipantSelected(participant.id as string),
          }">
            <div>
              <h2 class="text-normal-emphasis font-bold">{{ participant.name || `Hand ${index + 1}` }}</h2>
              <div class="font-sm text-normal-reduced">
                {{ formatCurrency(extraCardPrice) }}
              </div>
            </div>
            <div>
              <AppInputCheckbox :name="`select-item-${participant.id}`"
                :modelValue="isParticipantSelected(participant.id as string)"
                @update:modelValue="(value) => toggleParticipant(participant.id as string, value)" />

            </div>
          </div>
          <div class="w-[60px] text-right text-normal-emphasis font-medium">
            {{ formatCurrency(isParticipantSelected(participant.id as string) ? extraCardPrice : 0, false) }}
          </div>
        </div>
      </template>
      <AppExpandCollapse :showLabel="false" v-model="showAll">{{ showAll ? "Show fewer hands" : "Show more hands" }}
      </AppExpandCollapse>
      <p v-if="availableParticipants.length === 0" class="text-normal-reduced italic">
        All your hands already have extra cards.
      </p>

      <p class="text-sm text-normal-reduced" v-if="totalAssigned > 0">
        You may purchase extra cards until the third card is dealt.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventParticipant } from "@models/EventParticipant";
import { ref, computed, watch, onMounted } from "vue";
import useLocalization from "@/modules/useLocalization";
import { sortUtils } from "@/utils/sortUtils";
import AppExpandCollapse from "./AppExpandCollapse.vue";
import { RefSymbol } from "@vue/reactivity";
import AppInputCheckbox from "./AppInputCheckbox.vue";

const emit = defineEmits(["assignmentUpdated", "update:extraCardCount"]);
const props = defineProps({
  eventParticipants: {
    type: Array<EventParticipant>,
    required: true,
  },
  quantity: {
    type: Number,
    required: false,
    default: 1,
  },
  extraCardPrice: {
    type: Number,
    required: false,
    default: 0,
  },
  includeExtraCard: {
    type: Boolean,
    required: false,
    default: false
  }
});

const { formatCurrency } = useLocalization();

const assignedParticipantIds = ref<string[]>([]);
const showAll = ref(false)
const selectAll = ref(false);
const allowRaiseEvent = ref(true);


const availableParticipantsToShow = computed(() => {
  const filteredParticipants = availableParticipants.value;

  // Return at most 3 participants when showAll is false
  if (!showAll.value && filteredParticipants.length > 3) {
    return filteredParticipants.slice(0, 3);
  }
  return filteredParticipants;
});

// Filter out participants that already have an extra card
const availableParticipants = computed(() => {
  const participants = [...props.eventParticipants].filter(participant => !participant.extraCard);
  participants.sort(sortUtils.byTimestampAndId);
  for (let i = 0; i < participants.length; i++) {
    if (!participants[i].name) {
      participants[i].name = `Hand ${i + 1}`;
      i--;
    }
    if (selectAll.value) {
      toggleParticipant(participants[i].id as string, selectAll.value, false);
    }
  }

  return participants;
});

const totalAssigned = computed(() => {
  return assignedParticipantIds.value.length;
});

// Check if a participant is selected
const isParticipantSelected = (participantId: string) => {
  return assignedParticipantIds.value.includes(participantId);
};

const areAllSelected = computed(() => {
  return assignedParticipantIds.value.length === availableParticipants.value.length ?
    true : assignedParticipantIds.value.length === 0 ? false : null;
})

const toggleAllParticipant = (value: boolean) => {
  try {
    allowRaiseEvent.value = false;
    for (const participant of availableParticipants.value) {
      toggleParticipant(participant.id as string, value, false);
    }

    selectAll.value = value;
  }
  finally {
    allowRaiseEvent.value = true;
    raiseUpdateEvents();
  }

}

// Toggle a participant's selection
const toggleParticipant = (participantId: string, selected: boolean, raiseEvent: boolean = true) => {
  if (selected) {
    // Add to selected list if not already there
    if (!isParticipantSelected(participantId)) {
      assignedParticipantIds.value.push(participantId);
    }
  } else {
    // Remove from selected list
    assignedParticipantIds.value = assignedParticipantIds.value.filter(id => id !== participantId);
  }

  if (allowRaiseEvent.value) {
    selectAll.value = assignedParticipantIds.value.length === availableParticipants.value.length;
  }

  console.log("toggleParticipant", selectAll.value)
  raiseUpdateEvents();
};

const raiseUpdateEvents = () => {
  if (allowRaiseEvent.value) {
    emit("assignmentUpdated", assignedParticipantIds.value);
    emit("update:extraCardCount", totalAssigned.value);
  }
}
// Reset selections when quantity changes
watch(
  () => props.quantity,
  (value: number) => {
    if (value !== assignedParticipantIds.value.length) {
      assignedParticipantIds.value = [];
    }
  }
);

// Watch for includeExtraCard prop changes and select all participants when true
watch(
  () => props.includeExtraCard,
  (newValue: boolean) => {
    if (newValue && availableParticipants.value.length > 0) {
      toggleAllParticipant(true);
    }
  },
  { immediate: true } // Check on component initialization
);

</script>